import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { VeloceStatusBadge } from "./VeloceStatusBadge";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { AccountPreferences } from "../AccountPreferences";
import { ScrollToTop } from "../ScrollToTop";
import { History, WifiOff, RefreshCw } from "lucide-react";
import { useHistory } from "../../contexts/HistoryContext";
import { GlobalHistory } from "../GlobalHistory";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Toaster } from "../ui/sonner";
import { IconButton } from "../ui/button";
import useAuthStore from "../../stores/authStore";
import useLocationStore from "../../stores/locationStore";
import api from "../../services/api";
import type { Location } from "../../types";

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAccountPreferencesOpen, setIsAccountPreferencesOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const { movements } = useHistory();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const setLocations = useLocationStore((state) => state.setLocations);

  // Fetch locations on mount so X-Location-Id header gets set
  const { data: locationsData, isError: isLocationError, refetch: refetchLocations, isRefetching } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: async () => {
      const response = await api.get('/locations');
      return response.data.data;
    },
    retry: 2,
  });

  useEffect(() => {
    if (locationsData && Array.isArray(locationsData)) {
      setLocations(locationsData);
    }
  }, [locationsData, setLocations]);

  const getActiveTab = () => {
    const path = location.pathname.split("/")[1] || "inventaire";
    if (path === "" || path === "inventaire") return "inventaire";
    return path;
  };

  const handleTabChange = (tab: string) => {
    navigate(`/${tab}`);
  };

  const getLastUpdateText = () => {
    if (movements.length === 0) return "à l'instant";
    return format(movements[0].timestamp, "d MMM, HH:mm", { locale: fr });
  };

  const userName = user?.firstName || user?.email?.split("@")[0] || "Utilisateur";
  const userRole = user?.role === "ADMIN" ? "Administrateur" : user?.role === "MANAGER" ? "Gestionnaire" : user?.role === "STAFF" ? "Employé" : "Observateur";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--background)] via-[var(--background-subtle)] to-[var(--background)]">

      {/* Main Layout */}
      <div className="pb-20 md:pb-0">
        {/* Header - pleine largeur */}
        <header className="bg-[var(--card)] border-b border-[var(--border)] shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-6">
            <div className="flex items-center">
              {/* Logo aligné avec la sidebar sur desktop */}
              <div className="flex items-center justify-center w-auto md:w-16 lg:w-20 mr-2 md:mr-6 lg:mr-8 flex-shrink-0">
                <svg
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0 w-9 h-9 md:w-12 md:h-12"
                >
                  {/* Phone/Bubble shape */}
                  <rect
                    x="12"
                    y="6"
                    width="24"
                    height="36"
                    rx="4"
                    stroke="var(--color-steel-dark)"
                    strokeWidth="2.5"
                    fill="none"
                  />

                  {/* Isometric box - top face */}
                  <path
                    d="M24 18 L30 21 L24 24 L18 21 Z"
                    fill="var(--color-steel)"
                    stroke="var(--color-steel-dark)"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />

                  {/* Isometric box - left face */}
                  <path
                    d="M18 21 L18 30 L24 33 L24 24 Z"
                    fill="var(--color-steel-dark)"
                    stroke="var(--color-steel-dark)"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />

                  {/* Isometric box - right face */}
                  <path
                    d="M24 24 L24 33 L30 30 L30 21 Z"
                    fill="var(--color-steel-hover)"
                    stroke="var(--color-steel-dark)"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {/* Header Content - aligné avec le contenu principal */}
              <div className="flex items-center justify-between flex-1 gap-3 min-w-0">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                  <div className="min-w-0 flex flex-col">
                    <h1 className="text-base md:text-2xl font-bold text-[var(--foreground)] truncate leading-tight">
                      <span className="hidden md:inline">Outil de gestion d'inventaire</span>
                      <span className="md:hidden">Veloce</span>
                    </h1>
                    <Link
                      to="/changelog"
                      className="text-[9px] md:text-[10px] text-[var(--color-steel)] hover:text-[var(--color-steel-dark)] transition-colors font-medium uppercase tracking-wider hover:underline inline-block mt-0.5"
                      title={`Voir les nouveautés — commit ${__GIT_SHA__}`}
                    >
                      Version {__APP_VERSION__}
                    </Link>
                  </div>
                </div>
                {/* User Account Section */}
                <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                  {/* Status Indicator */}
                  <div className="flex items-center gap-1.5 md:gap-3">
                    <VeloceStatusBadge />
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full hidden md:block ${isLocationError ? 'bg-[var(--color-brick)]' : 'bg-[var(--color-olive)] animate-pulse'}`}></div>
                      <span className="hidden lg:inline text-sm text-[var(--muted-foreground)]">Dernière modification: {getLastUpdateText()}</span>
                      <IconButton
                        icon={History}
                        variant="ghost"
                        onClick={() => setIsHistoryOpen(true)}
                        title="Historique global"
                      />
                    </div>
                  </div>
                  {/* User Avatar & Settings */}
                  <div className="flex items-center gap-2 md:gap-3 border-l border-[var(--switch-background)] pl-2 md:pl-4">
                    <button
                      onClick={() => setIsAccountPreferencesOpen(true)}
                      className="flex items-center gap-2 md:gap-3 hover:bg-[var(--muted)] rounded-lg p-1.5 md:px-3 md:py-2 transition-colors group min-h-[44px] md:min-h-0"
                      title="Préférences du compte"
                    >
                      <div className="hidden sm:block text-right">
                        <div className="text-sm font-medium text-[var(--foreground)]">{userName}</div>
                        <div className="text-xs text-[var(--muted-foreground)]">{userRole}</div>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-[var(--color-steel-dark)] border-2 border-[var(--color-steel-dark)] group-hover:border-[var(--color-steel)] transition-colors flex-shrink-0 flex items-center justify-center text-[var(--foreground)] font-semibold text-sm">
                        {userName.charAt(0).toUpperCase()}
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Offline / Connection Error Banner */}
        {isLocationError && (
          <div className="bg-[var(--color-amber-bg)] border-b border-[var(--color-amber)]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-[var(--color-amber-dark)]">
                <WifiOff className="w-4 h-4 flex-shrink-0" />
                <span>Connexion au serveur impossible. Certaines données peuvent être obsolètes.</span>
              </div>
              <button
                onClick={() => refetchLocations()}
                disabled={isRefetching}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md bg-[var(--color-amber)] text-white hover:bg-[var(--color-amber-dark)] transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${isRefetching ? 'animate-spin' : ''}`} />
                Réessayer
              </button>
            </div>
          </div>
        )}

        {/* Container avec Sidebar + Main Content */}
        <div className="hidden md:flex max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Sidebar Desktop - sticky, suit le scroll */}
          <Sidebar activeTab={getActiveTab()} onTabChange={handleTabChange} />

          {/* Main Content */}
          <main className="flex-1 py-8 min-w-0">
            <Outlet />
          </main>
        </div>

        {/* Main Content Mobile (sans sidebar) */}
        <main className="md:hidden px-4 sm:px-6 py-8">
          <Outlet />
        </main>

        {/* Sidebar Mobile - bottom navigation */}
        <div className="md:hidden">
          <Sidebar activeTab={getActiveTab()} onTabChange={handleTabChange} />
        </div>

        {/* Account Preferences Dialog */}
        <AccountPreferences
          isOpen={isAccountPreferencesOpen}
          onClose={() => setIsAccountPreferencesOpen(false)}
          userName={userName}
          onLogout={logout}
        />

        {/* Global History Modal */}
        <GlobalHistory
          movements={movements}
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
        />

        {/* Scroll to Top Button */}
        <ScrollToTop />
      </div>

      {/* Toaster pour les notifications */}
      <Toaster />
    </div>
  );
}

export default Layout;
